import { useEffect, useRef } from 'react';

/**
 * Prefixes the browser tab title with `(N) ` when the user has unread Bonusly
 * or Bizy notifications, restoring the original title when the count clears.
 *
 * Counts above 9 collapse to `9+` to match the existing badge convention used
 * throughout the navbar (`bizyUnreadCount > 9 ? '9+' : bizyUnreadCount`).
 *
 * The first time the hook activates we capture the current `document.title`
 * as the base. Bonusly doesn't drive per-route titles today, so the captured
 * base is the page-load title (set by Rails or the SPA `index.html`).
 */

function formatBadge(count: number): string {
	return count > 9 ? '9+' : String(count);
}

function stripBadge(title: string): string {
	return title.replace(/^\(\d+\+?\)\s+/, '');
}

export function useNotificationTitle(unreadCount: number): void {
	const baseTitleRef = useRef<string | null>(null);

	useEffect(() => {
		if (typeof document === 'undefined') return;

		if (baseTitleRef.current === null) {
			baseTitleRef.current = stripBadge(document.title);
		}

		const baseTitle = baseTitleRef.current;
		document.title = unreadCount > 0 ? `(${formatBadge(unreadCount)}) ${baseTitle}` : baseTitle;
	}, [unreadCount]);

	useEffect(() => {
		return () => {
			if (baseTitleRef.current === null) return;
			document.title = baseTitleRef.current;
		};
	}, []);
}
